/* ============================================================
   PromptVault — pv-home.js
   Vanilla JS. No build step, no framework. Handles:
   1) Matrix rain background canvas
   2) Typing effect in the hero terminal
   3) Launch-offer countdown (persisted in localStorage)
   4) Interactive prompt browser (categories / items / detail)
   5) Copy-to-clipboard buttons
   6) Parking the bundle block's ambient motion while it is off-screen
   7) Hero animation: one prompt fanning out into six roles
   Bilingual (EN/ES): translatable strings are { en, es } objects and the
   UI re-renders on the 'pv:langchange' event fired by the floating switch.
   ============================================================ */

/* ---------- i18n helpers ---------- */
function L() {
  return (window.PVLang && PVLang.get()) || document.documentElement.getAttribute('data-pv-lang') || 'en';
}
/* t(x): if x is an { en, es } object, pick the active language; else return x. */
function t(x) {
  if (x && typeof x === 'object' && !Array.isArray(x)) return x[L()] || x.en;
  return x;
}

const UI = {
  en: {
    promptTitle: 'prompt · ',
    p01: '01 · the problem',
    p02: '02 · the exact prompt',
    p03: '03 · what Claude does',
    p04: '04 · the result',
    p05: '05 · how to adapt it',
    copy: 'copy',
    copied: 'copied ✓',
    casesShow: 'open the %n use cases',
    casesHide: 'hide the use cases',
    free: 'FREE',
    lockedText: 'This case lives inside the course, broken into its 5 parts and ready to copy. Unlock it with the rest of the course.',
    seeCourses: 'See the courses →'
  },
  es: {
    promptTitle: 'prompt · ',
    p01: '01 · el problema',
    p02: '02 · el prompt exacto',
    p03: '03 · qué hace Claude',
    p04: '04 · el resultado',
    p05: '05 · cómo adaptarlo',
    copy: 'copiar',
    copied: 'copiado ✓',
    casesShow: 'abrir los %n casos de uso',
    casesHide: 'ocultar los casos de uso',
    free: 'GRATIS',
    lockedText: 'Este caso vive dentro del curso, desglosado en sus 5 partes y listo para copiar. Desbloquéalo con el resto del curso.',
    seeCourses: 'Ver los cursos →'
  }
};
function ui(key) { return (UI[L()] || UI.en)[key]; }

/* ---------- DATA: the courses + sample prompts ----------
   Only items with `free: true` show their full contents.
   Locked items render the paywall panel. Edit freely.
   Translatable fields are { en, es } objects.                */
const CATALOG = [
  {
    id: 'master', label: { en: 'Ecommerce + AI Ads', es: 'Ecommerce y AI Ads' }, badge: 'ea',
    items: [
      {
        name: { en: 'Build landing pages', es: 'Crear landing pages' }, free: true,
        problem: {
          en: "I need a product landing page for Shopify that converts, but I don't know where to start and don't want to pay a designer.",
          es: 'Necesito una landing de producto para Shopify que convierta, pero no sé por dónde empezar y no quiero pagar a un diseñador.'
        },
        prompt: {
          en: 'Act as a copywriter and Shopify developer. Build a product landing page for [PRODUCT] aimed at [AUDIENCE].\nInclude: hero with value proposition, 3 benefits, social proof, FAQ and CTA.\nReturn it as a Liquid section ready to paste into my theme, mobile-first and with inline styles.',
          es: 'Actúa como copywriter y desarrollador de Shopify. Crea una landing de producto para [PRODUCTO] dirigida a [AUDIENCIA].\nIncluye: hero con propuesta de valor, 3 beneficios, prueba social, FAQ y CTA.\nDevuélvela como una sección Liquid lista para pegar en mi tema, mobile-first y con estilos inline.'
        },
        does: {
          en: 'Generates the full Liquid block (section + schema), writes the persuasive copy and applies responsive styles without you touching code.',
          es: 'Genera el bloque Liquid completo (sección + schema), escribe el copy persuasivo y aplica estilos responsive sin que toques código.'
        },
        result: {
          en: 'A .liquid file ready to upload to your theme and editable from the Shopify customizer.',
          es: 'Un archivo .liquid listo para subir a tu tema y editable desde el personalizador de Shopify.'
        },
        adapt: {
          en: 'Change [PRODUCT] and [AUDIENCE]. Add "use my palette #xxxxxx" or "include a size chart" to fit your store.',
          es: 'Cambia [PRODUCTO] y [AUDIENCIA]. Añade "usa mi paleta #xxxxxx" o "incluye una guía de tallas" para adaptarla a tu tienda.'
        }
      },
      { name: { en: 'Improve Shopify speed', es: 'Mejorar la velocidad de Shopify' }, locked: true },
      { name: { en: 'Review Liquid code', es: 'Revisar código Liquid' }, locked: true },
      { name: { en: 'Build simple apps', es: 'Crear apps sencillas' }, locked: true },
      { name: { en: 'Automate emails', es: 'Automatizar emails' }, locked: true },
      { name: { en: 'Optimize SEO', es: 'Optimizar el SEO' }, locked: true },
      {
        name: { en: 'Funnels', es: 'Funnels' }, free: true,
        problem: {
          en: "I want to launch a product but I'm not clear on the funnel or the follow-up emails.",
          es: 'Quiero lanzar un producto pero no tengo claro el funnel ni los emails de seguimiento.'
        },
        prompt: {
          en: 'Act as a direct-response marketing strategist. Design a complete funnel to sell [PRODUCT] to [AUDIENCE] at [PRICE].\nInclude: lead magnet, capture page, 5-email sequence, sales page and order bump.\nReturn it as an actionable plan with the copy for each step.',
          es: 'Actúa como estratega de marketing de respuesta directa. Diseña un funnel completo para vender [PRODUCTO] a [AUDIENCIA] a [PRECIO].\nIncluye: lead magnet, página de captación, secuencia de 5 emails, página de ventas y order bump.\nDevuélvelo como un plan accionable con el copy de cada paso.'
        },
        does: {
          en: 'Maps the funnel end to end and writes the copy for each page and email.',
          es: 'Mapea el funnel de principio a fin y escribe el copy de cada página y email.'
        },
        result: {
          en: 'A document with the step-by-step funnel and all the copy ready to paste into your tool.',
          es: 'Un documento con el funnel paso a paso y todo el copy listo para pegar en tu herramienta.'
        },
        adapt: {
          en: 'Change [PRODUCT], [PRICE] and the number of emails. Ask for the Meta Ads version or to connect GoHighLevel.',
          es: 'Cambia [PRODUCTO], [PRECIO] y el número de emails. Pide la versión para Meta Ads o conectar GoHighLevel.'
        }
      },
      { name: { en: 'Meta Ads', es: 'Meta Ads' }, locked: true },
      { name: { en: 'Google Ads', es: 'Google Ads' }, locked: true },
      { name: { en: 'Email marketing', es: 'Email marketing' }, locked: true }
    ]
  },
  {
    id: 'youtube', label: { en: 'YouTube', es: 'YouTube' }, badge: 'yt',
    items: [
      {
        name: { en: 'Automate scripts', es: 'Automatizar guiones' }, free: true,
        problem: {
          en: 'It takes me hours to write each video script and that kills my publishing rhythm.',
          es: 'Tardo horas en escribir cada guion de vídeo y eso mata mi ritmo de publicación.'
        },
        prompt: {
          en: 'You are a YouTube scriptwriter and retention expert. Write the script for a [DURATION] video about [TOPIC] for a [NICHE] channel.\nStructure: hook in the first 8s, promise, 3 points with examples and a final CTA.\nMark the B-roll points and the cuts. Tone: [TONE].',
          es: 'Eres guionista de YouTube y experto en retención. Escribe el guion de un vídeo de [DURACIÓN] sobre [TEMA] para un canal de [NICHO].\nEstructura: gancho en los primeros 8s, promesa, 3 puntos con ejemplos y un CTA final.\nMarca los puntos de B-roll y los cortes. Tono: [TONO].'
        },
        does: {
          en: 'Generates the script with timestamps, B-roll suggestions and cuts planned for editing.',
          es: 'Genera el guion con marcas de tiempo, sugerencias de B-roll y los cortes planificados para la edición.'
        },
        result: {
          en: 'A Markdown script ready for the teleprompter or to import into your editor.',
          es: 'Un guion en Markdown listo para el teleprompter o para importar en tu editor.'
        },
        adapt: {
          en: 'Adjust [DURATION], [NICHE] and [TONE]. Ask at the end for "5 titles and 3 thumbnail ideas".',
          es: 'Ajusta [DURACIÓN], [NICHO] y [TONO]. Pide al final "5 títulos y 3 ideas de miniatura".'
        }
      },
      { name: { en: 'Build editing workflows', es: 'Crear flujos de edición' }, locked: true },
      { name: { en: 'Organize projects', es: 'Organizar proyectos' }, locked: true },
      { name: { en: 'Automate thumbnails', es: 'Automatizar miniaturas' }, locked: true }
    ]
  },
  {
    id: 'code', label: { en: 'Code', es: 'Código' }, badge: 'pr',
    items: [
      {
        name: { en: 'Build a SaaS', es: 'Crear un SaaS' }, free: true,
        problem: {
          en: "I have a micro-SaaS idea but I don't know how to structure the project or the MVP.",
          es: 'Tengo una idea de micro-SaaS pero no sé cómo estructurar el proyecto ni el MVP.'
        },
        prompt: {
          en: 'Act as a senior full-stack engineer. Create the skeleton of a micro-SaaS for [IDEA] with Next.js, authentication, database and Stripe.\nGenerate the folder structure, data models and basic endpoints.\nExplain each decision and leave TODOs where customization is needed.',
          es: 'Actúa como ingeniero full-stack senior. Crea el esqueleto de un micro-SaaS para [IDEA] con Next.js, autenticación, base de datos y Stripe.\nGenera la estructura de carpetas, los modelos de datos y los endpoints básicos.\nExplica cada decisión y deja TODOs donde haga falta personalizar.'
        },
        does: {
          en: 'Creates the project structure, base files and auth + payments setup.',
          es: 'Crea la estructura del proyecto, los archivos base y la configuración de auth + pagos.'
        },
        result: {
          en: 'A working starter repo you can boot with npm run dev.',
          es: 'Un repo inicial funcional que puedes arrancar con npm run dev.'
        },
        adapt: {
          en: 'Change [IDEA] and the stack. Ask to "add tests" or "prep it to deploy on Vercel".',
          es: 'Cambia [IDEA] y el stack. Pide "añade tests" o "prepáralo para desplegar en Vercel".'
        }
      },
      { name: { en: 'Automations', es: 'Automatizaciones' }, locked: true },
      { name: { en: 'APIs', es: 'APIs' }, locked: true },
      { name: { en: 'Bots', es: 'Bots' }, locked: true }
    ]
  }
];

const TYPE_LINES = {
  en: [
    'claude, build my product page and put it in my store',
    'claude, write the script for my next video',
    'claude, write the abandoned-cart email for this week',
    'claude, turn my idea into an app that works'
  ],
  es: [
    'claude, monta la landing de mi producto en mi tienda',
    'claude, escribe el guion de mi próximo vídeo',
    'claude, manda el email de carritos abandonados',
    'claude, convierte mi idea en una app que funciona'
  ]
};
function typeLines() { return TYPE_LINES[L()] || TYPE_LINES.en; }

/* small helpers */
const $ = (sel, root = document) => root.querySelector(sel);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/* colorable padlock icon (inherits currentColor) */
const LOCK = (size) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="10.5" width="16" height="10" rx="2.2"></rect><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"></path></svg>`;

function copyToClipboard(text, btn) {
  try { navigator.clipboard && navigator.clipboard.writeText(text); } catch (e) {}
  if (!btn) return;
  const prev = btn.textContent;
  btn.textContent = ui('copied');
  clearTimeout(btn._t);
  btn._t = setTimeout(() => { btn.textContent = prev; }, 1500);
}

/* ---------- 1) Matrix rain ---------- */
function initMatrix() {
  const c = $('#matrix');
  if (!c) return;
  const ctx = c.getContext('2d');
  const glyphs = ['0','1','2','3','4','5','6','7','8','9','a','b','c','d','e','f','{','}','[',']','(',')','<','>','/','\\','|','&','!','=','+','-','*','%','^','~','?',':',';','.','#','$','_','=>','!=','==','&&','||','//','::','->','+=','<=','>=','</','/>','{}','()','[]'];
  const fs = 15, gap = fs + 4;
  let W = 0, H = 0, dpr = 1, cols = [];
  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = c.clientWidth || window.innerWidth;
    H = c.clientHeight || window.innerHeight;
    c.width = W * dpr; c.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const n = Math.floor(W / (fs + 8));
    cols = [];
    for (let i = 0; i < n; i++) {
      cols.push({
        x: i * (fs + 8) + 6,
        y: Math.random() * -H,
        speed: 0.4 + Math.random() * 0.8,
        len: 7 + Math.floor(Math.random() * 9),
        seed: Math.floor(Math.random() * glyphs.length)
      });
    }
  }
  pvOnResize(resize);
  resize();
  function draw() {
    ctx.clearRect(0, 0, W, H);
    ctx.font = fs + "px 'JetBrains Mono', monospace";
    for (const col of cols) {
      for (let k = 0; k < col.len; k++) {
        const yy = col.y - k * gap;
        if (yy < -gap || yy > H) continue;
        const t = 1 - k / col.len;
        ctx.fillStyle = k === 0 ? 'rgba(190,255,214,0.55)' : 'rgba(79,232,132,' + (t * 0.32).toFixed(3) + ')';
        const idx = (Math.floor(yy / gap) + col.seed + col.x) % glyphs.length;
        ctx.fillText(glyphs[(idx + glyphs.length) % glyphs.length], col.x, yy);
      }
      col.y += col.speed * 3.6;
      if (col.y - col.len * gap > H) {
        col.y = Math.random() * -120;
        col.speed = 0.4 + Math.random() * 0.8;
        col.len = 7 + Math.floor(Math.random() * 9);
        col.seed = Math.floor(Math.random() * glyphs.length);
      }
    }
  }
  pvBgLoop(draw);
}

/* ---------- 2) Typing effect ----------
   Dormant: #typed lived in the hero terminal card, which the hero animation
   replaced. Left in place (it no-ops when the element is absent) because the
   lines in TYPE_LINES are worth reusing. */
function initTyping() {
  const el = $('#typed');
  if (!el) return;
  let li = 0, ci = 0, del = false;
  function tick() {
    const lines = typeLines();
    const full = lines[li % lines.length];
    if (ci > full.length) ci = full.length; // clamp after a language switch
    if (!del) {
      ci++;
      el.textContent = full.slice(0, ci);
      if (ci >= full.length) { del = true; return setTimeout(tick, 1500); }
    } else {
      ci--;
      el.textContent = full.slice(0, ci);
      if (ci <= 0) { del = false; li = (li + 1) % lines.length; }
    }
    setTimeout(tick, del ? 26 : 52);
  }
  setTimeout(tick, 500);
}

/* ---------- 3) Countdown ---------- */
function initCountdown() {
  const el = $('#countdown-time');
  if (!el) return;
  const hours = parseInt(el.dataset.hours, 10) || 47;
  let dl = +localStorage.getItem('pv_deadline');
  const now = Date.now();
  if (!dl || dl < now) { dl = now + hours * 3600 * 1000; localStorage.setItem('pv_deadline', String(dl)); }
  const p = (n) => String(n).padStart(2, '0');
  function upd() {
    let s = Math.max(0, Math.floor((dl - Date.now()) / 1000));
    const d = Math.floor(s / 86400); s %= 86400;
    const h = Math.floor(s / 3600); s %= 3600;
    const m = Math.floor(s / 60); s %= 60;
    el.textContent = p(d) + 'd ' + p(h) + ':' + p(m) + ':' + p(s);
  }
  upd();
  setInterval(upd, 1000);
}

/* ---------- 4) Prompt browser ---------- */
/* open  = the case list is unfolded (it starts behind the toggle button).
   item === null = no case selected, so no panel on screen. That is where the
   section starts at every width, and where it goes back to when the list is
   folded away again. */
const browserState = { cat: 'master', item: null, open: false };
/* true for the one render right after the toggle opens, so the cases unfold
   once instead of re-staggering every time you tap one of them. */
let casesUnfolding = false;

function renderCats() {
  const wrap = $('#browser-cats');
  if (!wrap) return;
  wrap.innerHTML = CATALOG.map((c) => `
    <button class="cat-btn${c.id === browserState.cat ? ' is-active' : ''}" data-cat="${c.id}" data-course="${c.id}">
      <span class="cat-btn__badge">${esc(c.badge)}</span>${esc(t(c.label))}
    </button>`).join('');
  wrap.querySelectorAll('.cat-btn').forEach((b) => {
    b.addEventListener('click', () => {
      browserState.cat = b.dataset.cat;
      /* Folded again on every course change: the new course brings its own
         animation, and the list opens again when that one has played. */
      browserState.open = false;
      browserState.item = null;
      renderBrowser();
      replayCourseAnim();
    });
  });
}

/* ---- the course animation ----
   Each course can bring its own animation snippet, tagged data-anim="<course
   id>"; only the selected course's is on screen. The animations are third
   party in spirit: their own markup, their own css, their own script, none of
   it touched here. What is controlled from here is when they start, which is
   never on load: the script is injected the first time the browser is actually
   on screen with that course selected. Off-screen or on another course, the
   file is not even fetched. */
let stageSeen = false;   // the browser has been scrolled into view at least once
let stageCourse = null;  // the course the auto-open below is counting for
let casesTimer = null;   // pending auto-open, cancelled the moment it is stale

function renderStage() {
  const stage = $('#browser-stage');
  const box = document.querySelector('.pv .browser');
  if (!stage) return;
  let live = null;
  stage.querySelectorAll('.course-anim').forEach((a) => {
    const on = a.getAttribute('data-anim') === browserState.cat;
    a.hidden = !on;
    if (on) live = a;
  });
  // no animation for this course yet: the row folds away entirely
  if (box) box.classList.toggle('has-anim', !!live);
  fitStage();
  bootStage();
  // a course change restarts the count; anything else (a case tapped, a
  // language switch) leaves the pending one alone
  if (stageCourse !== browserState.cat) { stageCourse = browserState.cat; armCases(live); }
}

/* These animations are authored on a fixed canvas (960 wide, 430 or 530 tall)
   and used to be cropped, with a horizontal scrollbar, on anything
   narrower. So scale the canvas to the width the section actually has: a
   transform keeps every coordinate inside the animation exactly where its
   author put it, which re-laying it out never would. offsetWidth is read, not
   getBoundingClientRect, because it reports the layout size and ignores the
   transform we are about to set, so re-fitting never compounds.
   The holder gets the scaled height, since a transform leaves the box it came
   from behind in the layout. .is-fit is what turns the original horizontal
   scroll off: with JS down, the animation keeps scrolling as it shipped. */
function fitStage() {
  const stage = $('#browser-stage');
  if (!stage) return;
  stage.querySelectorAll('.course-anim').forEach((anim) => {
    if (anim.hidden) return;                 // a hidden box measures 0
    /* Usually one canvas per animation. The code course ships two, a wide
       stage and a phone stage with their own coordinates, and a media query
       displays one of them: the other reports a width of 0 here and is
       skipped, so this walks all of them rather than taking the first. */
    anim.querySelectorAll('[data-fit]').forEach((canvas) => {
      const holder = canvas.parentElement;
      if (!holder) return;
      const w = canvas.offsetWidth, h = canvas.offsetHeight;
      if (!w || !h) return;
      anim.classList.add('is-fit');
      /* Up as well as down: the stage is 960 wide and the section gives it
         about 1130, so on a desktop it is drawn a sixth bigger rather than
         floating in its own margin. Capped so it can never turn into a poster
         if the layout ever gets wider than that. */
      const k = Math.min(1.25, holder.clientWidth / w);
      /* k === 1 is the phone layout, where the animation lays itself out to
         the width it has: no transform, and no pinned height either, so it is
         free to be as tall as its own content ends up being. */
      const tr = k === 1 ? '' : 'scale(' + k + ')';
      const ht = k === 1 ? '' : Math.round(h * k) + 'px';
      // Written only when they actually change: this runs from a ResizeObserver
      // and the height it sets is inside what that observer is watching.
      if (canvas.style.transform !== tr) canvas.style.transform = tr;
      if (holder.style.height !== ht) holder.style.height = ht;
    });
  });
}

function initStageFit() {
  const stage = $('#browser-stage');
  if (!stage) return;
  if ('ResizeObserver' in window) {
    // fires once on observe, which also covers the stylesheet landing late
    new ResizeObserver(fitStage).observe(stage);
  } else {
    window.addEventListener('resize', fitStage);
  }
  window.addEventListener('load', fitStage);
}

function bootStage() {
  if (!stageSeen) return;
  const stage = $('#browser-stage');
  if (!stage) return;
  const live = stage.querySelector('.course-anim:not([hidden])');
  if (!live || live.dataset.booted) return;
  const src = live.getAttribute('data-anim-src');
  if (!src) return;
  live.dataset.booted = '1';
  const tag = document.createElement('script');
  tag.src = src;
  /* The count for the auto-open restarts here: the animation begins on the
     line that runs this file, not on the line that asked for it, and the fetch
     in between is not part of its cycle. */
  tag.addEventListener('load', () => {
    if (browserState.cat === live.getAttribute('data-anim')) armCases(live);
  });
  document.body.appendChild(tag);
}

/* The animation is the pitch and the cases are the payoff, so the cases let it
   finish talking and then open on their own: one full cycle of whichever
   animation is on screen (each snippet carries its own length in
   data-anim-cycle), counted from the moment it starts, which is the moment the
   section is first on screen. A course with no animation has nothing to wait
   for and opens straight away.
   Cancelled by anyone who gets there first: if the visitor presses the button
   themselves, the timer is dropped rather than firing over them later. */
/* Each animation plays once and freezes on its last frame; picking a course
   is the only thing that plays it again, this course included, so pressing the
   chip you are already on replays it. The animation registers itself under
   window.PVCourseAnim with the key its snippet names in data-anim-play, and
   the count for the auto-open starts over with it. If its script has not been
   fetched yet there is nothing to replay: bootStage is about to run it for the
   first time, and its load handler does both jobs. */
function replayCourseAnim() {
  const stage = $('#browser-stage');
  const live = stage && stage.querySelector('.course-anim:not([hidden])');
  if (!live) return;
  const reg = window.PVCourseAnim && window.PVCourseAnim[live.getAttribute('data-anim-play')];
  if (!reg || !reg.play) return;
  reg.play();
  armCases(live);
}

function clearCasesTimer() {
  if (casesTimer) { clearTimeout(casesTimer); casesTimer = null; }
}

function armCases(anim) {
  clearCasesTimer();
  if (!stageSeen || browserState.open) return;
  const ms = anim ? parseInt(anim.getAttribute('data-anim-cycle'), 10) : 0;
  casesTimer = setTimeout(() => {
    casesTimer = null;
    if (!browserState.open) setCasesOpen(true);
  }, ms > 0 ? ms : 0);
}

function initStageObserver() {
  const box = document.querySelector('.pv .browser');
  const stage = $('#browser-stage');
  if (!box) return;
  if (!('IntersectionObserver' in window)) {
    stageSeen = true; bootStage();
    if (stage) armCases(stage.querySelector('.course-anim:not([hidden])'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      stageSeen = true;
      bootStage();
      armCases(stage.querySelector('.course-anim:not([hidden])'));
      io.disconnect();          // one shot: it only ever needs the first arrival
    });
  }, { rootMargin: '0px 0px -12% 0px' });
  io.observe(box);
}

/* The case list is the payoff of this section, so it is not just sitting
   there: it opens from a button that pulses until it has been used once. The
   count comes from the selected course, so the button always says how much is
   behind it. */
function renderCasesToggle() {
  const btn = $('#cases-toggle');
  if (!btn) return;
  const cur = CATALOG.find((c) => c.id === browserState.cat) || CATALOG[0];
  const label = browserState.open
    ? ui('casesHide')
    : ui('casesShow').replace('%n', cur.items.length);
  btn.innerHTML =
    '<span class="cases-toggle__chev" aria-hidden="true">&#9656;</span>' +
    '<span class="cases-toggle__txt">' + esc(label) + '</span>' +
    '<span class="cases-toggle__n">' + cur.items.length + '</span>';
  btn.setAttribute('aria-expanded', browserState.open ? 'true' : 'false');
  btn.classList.toggle('is-open', browserState.open);
}

function setCasesOpen(on) {
  browserState.open = on;
  casesUnfolding = on;
  const btn = $('#cases-toggle');
  if (btn && on) btn.classList.remove('is-idle');   // found; stop pulsing
  /* Opening on desktop opens the first case with it: the panel is a whole grid
     column over there and an empty one would just be a hole. On mobile the
     panel is a row inside the list, so nothing opens until you pick a case. */
  browserState.item = on && !mqBrowser.matches ? 0 : null;
  renderBrowser();
}

function toggleCases() {
  clearCasesTimer();           // the visitor got there first
  setCasesOpen(!browserState.open);
}

function renderItems() {
  const wrap = $('#browser-items');
  if (!wrap) return;
  /* On mobile #browser-detail is parked inside this list (placeBrowserDetail),
     and the innerHTML below would delete it: the first tap on a case wiped the
     panel out of the document and nothing ever opened again. Move it back to
     the grid first; placeBrowserDetail files it in again at the end of the
     render. */
  const detail = $('#browser-detail');
  const box = document.querySelector('.pv .browser');
  if (detail && box && detail.parentElement === wrap) box.appendChild(detail);
  const cur = CATALOG.find((c) => c.id === browserState.cat) || CATALOG[0];
  wrap.innerHTML = cur.items.map((it, i) => `
    <button class="item-btn${i === browserState.item ? ' is-active' : ''}" data-item="${i}" style="--i:${i}">
      <span>${esc(t(it.name))}</span>
      ${it.free ? '<span class="free">' + esc(ui('free')) + '</span>' : ''}
      ${it.locked ? '<span class="lock">' + LOCK(13) + '</span>' : ''}
    </button>`).join('');
  wrap.classList.toggle('is-open', browserState.open);
  wrap.classList.toggle('is-unfolding', casesUnfolding);
  casesUnfolding = false;
  wrap.querySelectorAll('.item-btn').forEach((b) => {
    b.addEventListener('click', () => {
      const i = +b.dataset.item;
      // Accordion on mobile: tapping the open case closes it again.
      browserState.item = (mqBrowser.matches && browserState.item === i) ? null : i;
      renderBrowser();
    });
  });
}

/* Opening a case plays like a terminal: the window snaps open, the title bar
   types the command out with a caret, and the body prints one block at a time.
   Everything but the typing lives in CSS (.is-booting / .is-in); this only
   drives the characters, so nothing animates unless JS is running. */
let detailType = null;   // in-flight typing timer, cancelled if you tap again
let detailKey  = null;   // last case actually painted, so a language switch
                         // (which re-renders the same case) does not replay it

function bootDetail(wrap, key) {
  if (detailType) { clearInterval(detailType); detailType = null; }
  const title = wrap.querySelector('.detail__title');
  const body  = wrap.querySelector('.detail__body, .locked');
  const first = detailKey === null;
  const same  = detailKey === key;
  detailKey = key;
  // First paint of the page and re-renders of the same case: no theatre.
  if (first || same || !title || !body || prefersReduce()) {
    // No animation: drop .is-booting too, or the CSS would keep the blocks it
    // hides for the boot hidden for good.
    wrap.classList.remove('is-booting');
    if (body) body.classList.add('is-in');
    return;
  }
  wrap.classList.remove('is-booting');
  void wrap.offsetWidth;                 // restart the CSS animation
  wrap.classList.add('is-booting');
  const text = title.textContent;
  title.textContent = '';
  title.classList.add('is-typing');
  let i = 0;
  detailType = setInterval(() => {
    i = Math.min(i + 2, text.length);    // two chars a tick reads as fast typing
    title.textContent = text.slice(0, i);
    if (i >= text.length) {
      clearInterval(detailType); detailType = null;
      title.classList.remove('is-typing');
      body.classList.add('is-in');       // now the output prints
    }
  }, 18);
}

function prefersReduce() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function renderDetail() {
  const wrap = $('#browser-detail');
  if (!wrap) return;
  // Nothing selected: empty the panel and take it out of the flow, so a closed
  // list is a list of cases and nothing else.
  if (browserState.item === null) {
    wrap.innerHTML = ''; wrap.hidden = true; wrap.classList.remove('is-booting');
    // 'closed', not null: null means the page has not painted the panel yet
    // (no animation on first load), while reopening after a close should boot.
    detailKey = 'closed'; return;
  }
  wrap.hidden = false;
  const cur = CATALOG.find((c) => c.id === browserState.cat) || CATALOG[0];
  const a = cur.items[browserState.item] || cur.items[0];
  const name = esc(t(a.name));

  if (a.locked) {
    wrap.innerHTML = `
      <div class="detail__bar">
        <span class="dot"></span><span class="dot"></span><span class="dot"></span>
        <span class="detail__title">${esc(ui('promptTitle'))}${name}</span>
      </div>
      <div class="locked">
        <div class="locked__icon">${LOCK(24)}</div>
        <div class="locked__name">${name}</div>
        <p>${esc(ui('lockedText'))}</p>
        <a href="#pricing" class="btn">${esc(ui('seeCourses'))}</a>
      </div>`;
    bootDetail(wrap, cur.id + ':' + browserState.item);
    return;
  }

  const prompt = t(a.prompt);
  wrap.innerHTML = `
    <div class="detail__bar">
      <span class="dot"></span><span class="dot"></span><span class="dot"></span>
      <span class="detail__title">${esc(ui('promptTitle'))}${name}</span>
    </div>
    <div class="detail__body">
      <div class="detail__name">${name}</div>

      <div class="block">
        <div class="block__label">${esc(ui('p01'))}</div>
        <p>${esc(t(a.problem))}</p>
      </div>

      <div class="block">
        <div class="block__row">
          <div class="block__label">${esc(ui('p02'))}</div>
          <button class="copy-btn" id="detail-copy">${esc(ui('copy'))}</button>
        </div>
        <div class="prompt-box" id="detail-prompt">${esc(prompt)}</div>
      </div>

      <div class="block-grid">
        <div class="block block--sm" style="margin-top:0">
          <div class="block__label">${esc(ui('p03'))}</div>
          <p>${esc(t(a.does))}</p>
        </div>
        <div class="block block--sm" style="margin-top:0">
          <div class="block__label">${esc(ui('p04'))}</div>
          <p>${esc(t(a.result))}</p>
        </div>
      </div>

      <div class="block--divider block--sm">
        <div class="block__label">${esc(ui('p05'))}</div>
        <p>${esc(t(a.adapt))}</p>
      </div>
    </div>`;

  bootDetail(wrap, cur.id + ':' + browserState.item);

  const btn = $('#detail-copy', wrap);
  if (btn) btn.addEventListener('click', () => copyToClipboard(prompt, btn));
}

/* Where the detail panel lives.
   Desktop is a three-column grid: categories, cases, panel, and the panel is
   the third child of .browser. Under 760px the grid collapses to one column,
   which put the panel below a list of twelve cases: you tapped a case and
   nothing appeared on screen. So at that width the panel is moved into the
   list, directly after the active case, and the list becomes an accordion.
   Only the parent changes; the panel keeps its markup, its id and its state. */
const mqBrowser = window.matchMedia('(max-width: 760px)');

function placeBrowserDetail() {
  const detail = $('#browser-detail');
  const box = document.querySelector('.pv .browser');
  const items = $('#browser-items');
  if (!detail || !box) return;
  if (mqBrowser.matches && items) {
    const active = items.querySelector('.item-btn.is-active');
    if (active) active.insertAdjacentElement('afterend', detail);
    else items.appendChild(detail);
  } else if (detail.parentElement !== box) {
    box.appendChild(detail);   // back to the third grid cell
  }
}

function renderBrowser() {
  // Paint the browser in the selected course's colours. The palettes live in
  // pv-home.css keyed by this same attribute, so the item list and the detail
  // panel inherit it; each category button overrides it with its own id.
  const box = document.querySelector('.pv .browser');
  if (box) {
    box.setAttribute('data-course', browserState.cat);
    // folded: the grid drops the case column so the button can take the width
    box.classList.toggle('is-collapsed', !browserState.open);
  }
  renderCats();
  renderStage();
  renderCasesToggle();
  renderItems();
  renderDetail();
  placeBrowserDetail();
}

/* Crossing the breakpoint with the panel parked in the wrong parent would
   leave it inside the list on desktop, so re-home it on every change. Widening
   out of the closed state also has to pick a case: the desktop grid keeps a
   third column open and an empty one would just be a hole. */
function syncBrowserBreakpoint() {
  if (!mqBrowser.matches && browserState.open && browserState.item === null) {
    browserState.item = 0; renderBrowser();
  }
  else placeBrowserDetail();
}
if (mqBrowser.addEventListener) mqBrowser.addEventListener('change', syncBrowserBreakpoint);
else if (mqBrowser.addListener) mqBrowser.addListener(syncBrowserBreakpoint);

/* ---------- 4b) FAQ accordion (mobile only) ----------
   The six <details> ship `open`, so with JS off, or on desktop, the FAQ is the
   flat list of questions and answers it has always been. Under 760px all six
   answers open at once is 1200px of page, so they get closed and the summary
   grows a + / x affordance. Re-synced only when the breakpoint is crossed, so
   a question the visitor opened themselves is never slammed shut under them. */
function initFaqAccordion() {
  const faqs = Array.prototype.slice.call(document.querySelectorAll('.pv details.faq'));
  if (!faqs.length) return;
  const mq = window.matchMedia('(max-width: 760px)');

  faqs.forEach((d) => {
    const q = d.querySelector('.faq__q');
    if (q && !q.querySelector('.faq__sign')) {
      const sign = document.createElement('span');
      sign.className = 'faq__sign';
      sign.setAttribute('aria-hidden', 'true');
      sign.textContent = '+';
      q.appendChild(sign);
    }
  });

  function sync() {
    faqs.forEach((d) => {
      // On mobile every question starts closed, the first one included.
      if (mq.matches) d.removeAttribute('open');
      else d.setAttribute('open', '');
    });
  }
  sync();
  if (mq.addEventListener) mq.addEventListener('change', sync);
  else if (mq.addListener) mq.addListener(sync);
}

/* ---------- 5) Generic copy buttons ([data-copy]) ---------- */
function initCopyButtons() {
  document.querySelectorAll('[data-copy]').forEach((btn) => {
    btn.addEventListener('click', () => copyToClipboard(btn.getAttribute('data-copy'), btn));
  });
}

/* ---------- 6) Ambient motion in the courses band ----------
   Four CSS animations live down there: the green-to-blue ribbon on the master
   course card, the white bubbles drifting up the YouTube card, the ribbon of
   course colors sweeping across the BEST VALUE card, and the levitating trust
   cards. All of them scroll a background or repaint a
   blurred shadow every frame, so tag them .is-off whenever they are not on
   screen and let CSS park them. Same spirit as pvBgLoop: the effect only costs
   anything while someone can see it. */
function initAmbientMotion() {
  if (!('IntersectionObserver' in window)) return;
  const els = document.querySelectorAll('.pv .course--master, .pv .course--yt, .pv .pack, .pv .trustbar');
  if (!els.length) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => e.target.classList.toggle('is-off', !e.isIntersecting));
  }, { rootMargin: '120px' });
  els.forEach((el) => { el.classList.add('is-off'); io.observe(el); });
}

/* ---------- 7) Hero animation ("one prompt, a whole team") ----------
   The timeline the standalone design ships with, with two changes made on the
   way in:
   - it runs through pvBgLoop (~30fps, parked while the tab is hidden or the
     cart drawer is open) instead of its own raw requestAnimationFrame, and
   - it stops itself the moment the timeline ends. The design plays once and
     holds its last frame, so after that the loop has nothing left to do; the
     logo pulse, the rings and the caret keep going in CSS alone.
   The stage keeps the design's own 960x460 and is scaled to whatever room the
   hero column has, up to HZ_MAX; under 760px the CSS stacks it and scale is
   off. Scaling is the only size lever: nothing inside the stage is resized. */
const HZ_PROMPT = { en: 'scale my business', es: 'escala mi negocio' };
const HZ_W = 960, HZ_H = 460;
const HZ_MAX = 1.12;          /* how far past its authored size the stage may go */
const HZ_END = 8400;          /* ms, last keyframe of the timeline */
const HZ_SPEED = 1;           /* > 1 fast-forwards the whole thing */
const HZ_START = [1900, 2550, 3200, 3850, 4500, 5150];  /* per-role kickoff */
const HZ_WORK = 1500;         /* ms a role spends "working" */
let hzRepaint = null;         /* re-renders the current frame after a language switch */

function initHeroAnim() {
  const stage = $('#hz-stage');
  const fitEl = $('#hz-fit');
  if (!stage || !fitEl) return;

  /* --- scale the fixed stage into the available width --- */
  function fit() {
    if (window.innerWidth <= 760) {     // CSS stacks it below this width
      stage.style.transform = '';
      fitEl.style.height = '';
      return;
    }
    const s = Math.min(HZ_MAX, fitEl.clientWidth / HZ_W);
    stage.style.transform = s < 1 ? `scale(${s})` : '';
    fitEl.style.height = Math.round(HZ_H * s) + 'px';
  }
  fit();
  if (window.pvOnResize) pvOnResize(fit);
  else window.addEventListener('resize', fit);

  /* --- elements + beam geometry ---
     Each beam's dash length is read off the path itself, so the lines can be
     re-aimed in pv-home.liquid without touching a number in here. */
  const measure = (p) => {
    const len = p.getTotalLength ? p.getTotalLength() : 0;
    p.style.strokeDasharray = len;
    return len;
  };
  const prm = $('#hz-prm'), paste = $('#hz-paste'), entBeam = $('#hz-beam-ent');
  const core = $('#hz-core'), cnt = $('#hz-cnt'), cntWrap = $('#hz-counter');
  const pill = $('#hz-pill'), sub = $('#hz-sub');
  const entLen = measure(entBeam);
  const chips = [], beams = [], runs = [], dones = [], tasks = [], lens = [];
  for (let i = 0; i < 6; i++) {
    const chip = $('.hz-c' + i, stage), beam = $('#hz-beam-' + i);
    chips.push(chip);
    beams.push(beam);
    runs.push($('.hz-run', chip));
    dones.push($('.hz-done', chip));
    tasks.push($('.hz-task', chip));
    lens.push(measure(beam));
  }

  /* --- easing --- */
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const S = (x, s, d) => clamp((x - s) / d, 0, 1);                       // ramp 0 → 1
  const E = (k) => (k >= 1 ? 1 : 1 - Math.pow(2, -9 * k) * Math.cos(k * 12));  // springy
  const EZ = (k) => 1 - Math.pow(1 - clamp(k, 0, 1), 3);                // ease-out cubic

  function render(ms) {
    const line = t(HZ_PROMPT);
    prm.textContent = line.slice(0, Math.floor(S(ms, 420, 300) * line.length));
    paste.style.opacity = S(ms, 400, 240) * (1 - S(ms, 1650, 350));
    entBeam.style.opacity = S(ms, 1350, 160) * (1 - S(ms, 1900, 400)) + 0.25 * S(ms, 1900, 400);
    entBeam.style.strokeDashoffset = entLen * (1 - S(ms, 1330, 200));

    const ck = S(ms, 1550, 380);
    core.style.opacity = ck;
    core.style.transform = `scale(${0.7 + 0.3 * E(ck)})`;
    cntWrap.style.opacity = ck;

    let done = 0;
    for (let i = 0; i < 6; i++) {
      const si = HZ_START[i], di = si + HZ_WORK, k = S(ms, si, HZ_WORK);
      if (ms >= di) done++;
      chips[i].style.opacity = S(ms, si - 60, 300);
      chips[i].style.transform = `scale(${0.75 + 0.25 * E(S(ms, si - 60, 420))})`;
      runs[i].style.opacity = (k > 0 && k < 1) ? 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(ms / 130 + i)) : 0;
      dones[i].style.opacity = S(ms, di, 220);
      tasks[i].style.opacity = 0.4 * S(ms, si + 280, 300) + 0.6 * S(ms, di, 260);
      beams[i].style.opacity = S(ms, si - 380, 140) *
        (k <= 0 ? 0.85 : k < 1 ? 0.5 + 0.5 * (0.5 + 0.5 * Math.sin(ms / 150 + i * 2)) : 0.32);
      beams[i].style.strokeDashoffset = lens[i] * (1 - EZ(S(ms, si - 380, 340)));
    }
    cnt.textContent = done + '/6';

    const rk = S(ms, 7050, 460);
    pill.style.opacity = rk;
    pill.style.transform = `scale(${0.6 + 0.4 * E(rk)})`;
    sub.style.opacity = S(ms, 7500, 420);
  }

  /* --- one-shot driver --- */
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let ms = 0, prev = 0, loop = null, started = false;
  hzRepaint = () => render(ms);

  function start() {
    if (started) return;
    started = true;
    if (reduce || typeof pvBgLoop !== 'function') { ms = HZ_END; render(ms); return; }
    loop = pvBgLoop(() => {
      const now = performance.now();
      if (!prev) prev = now;
      /* Clamped: a parked loop (hidden tab, open cart) has to hold the
         timeline where it was, not fast-forward through it on resume. */
      ms = Math.min(ms + Math.min(now - prev, 100) * HZ_SPEED, HZ_END);
      prev = now;
      render(ms);
      if (ms >= HZ_END) loop.stop();
    });
  }

  /* Nothing runs until it is on screen; the hero is at the top of the page,
     so in practice that means "once the first paint has landed". */
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); start(); }
    }, { rootMargin: '80px' });
    io.observe(stage);
  } else {
    start();
  }
}

/* ---------- boot ---------- */
document.addEventListener('DOMContentLoaded', () => {
  initMatrix();
  initTyping();
  initCountdown();
  renderBrowser();
  const casesBtn = $('#cases-toggle');
  if (casesBtn) casesBtn.addEventListener('click', toggleCases);
  initStageObserver();
  initStageFit();
  initCopyButtons();
  initFaqAccordion();
  initAmbientMotion();
  initHeroAnim();
});

/* re-render language-dependent UI when the floating switch changes language */
document.addEventListener('pv:langchange', renderBrowser);
/* the hero animation's typed prompt is the one string it draws itself; once
   the timeline has stopped, nothing would repaint it without this. */
document.addEventListener('pv:langchange', () => { if (hzRepaint) hzRepaint(); });
